import { ReactElement, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { DetailsPanel, FormatInformation, Table, Typography } from '@weareopenr/component-library';
import { DetailedInventoryItem } from 'src/api/getInventoryItem/types';
import styles from './InventoryDietaryAttributesTab.module.scss';

interface InventoryDietaryAttributesTabProps {
  inventoryItem: DetailedInventoryItem;
}

type TNutritionRow = {
  nutritionId: string;
  nutrient: string;
  value: string;
  valueLow: string;
  valueHigh: string;
};

const nutritionColumnHelper = createColumnHelper<TNutritionRow>();

const nutritionValueColumns = [
  nutritionColumnHelper.accessor('nutrient', { header: '' }),
  nutritionColumnHelper.accessor('value', { header: 'Value', size: 220 }),
];

const nutritionRangeColumns = [
  nutritionColumnHelper.accessor('nutrient', { header: '' }),
  nutritionColumnHelper.accessor('valueLow', { header: 'Value (Low)', size: 180 }),
  nutritionColumnHelper.accessor('valueHigh', { header: 'Value (High)', size: 180 }),
];

const formatNutritionName = (name: string) => {
  return name.replaceAll('_', ' ').replaceAll(/\b\w/g, (character) => character.toUpperCase());
};

const getAllergens = (inventoryItem: DetailedInventoryItem) => {
  return inventoryItem.allergens_information
    .map((allergen) => {
      const childAllergens = allergen.child_allergens
        ? ` (${allergen.child_allergens.map((childAllergen) => childAllergen.name).join(', ')})`
        : '';
      return `${allergen.name}${childAllergens}`;
    })
    .join(', ');
};

const getClaims = (inventoryItem: DetailedInventoryItem) => {
  const payloadClaims = Array.isArray(inventoryItem.claims) ? inventoryItem.claims : [];
  const inferredClaims = [
    inventoryItem.is_vegan ? 'Vegan' : null,
    inventoryItem.is_vegetarian ? 'Vegetarian' : null,
  ].filter((claim): claim is string => Boolean(claim));

  const claims = Array.from(new Set([...payloadClaims, ...inferredClaims]));

  return claims.join(', ') || 'No Claims';
};

const NutritionEmptyState = () => (
  <div className={styles['table-empty-state']}>
    <Typography colour="primary">No nutritional information found</Typography>
  </div>
);

const InventoryDietaryAttributesTab = ({ inventoryItem }: InventoryDietaryAttributesTabProps): ReactElement => {
  const nutritionRows = useMemo<TNutritionRow[]>(
    () =>
      [...inventoryItem.nutritional_information]
        .sort((a, b) => {
          const aIsEnergy = a.name.toLowerCase().startsWith('energy');
          const bIsEnergy = b.name.toLowerCase().startsWith('energy');
          if (aIsEnergy && !bIsEnergy) return -1;
          if (!aIsEnergy && bIsEnergy) return 1;
          return formatNutritionName(a.name).localeCompare(formatNutritionName(b.name));
        })
        .map((nutrition) => ({
          nutritionId: nutrition.nutrition_id,
          nutrient: formatNutritionName(nutrition.name),
          value: `${nutrition.value_high}`,
          valueLow: `${nutrition.value_low}`,
          valueHigh: `${nutrition.value_high}`,
        })) || [],
    [inventoryItem],
  );

  const hasSingleNutritionValue = useMemo(
    () =>
      inventoryItem.nutritional_information.every((nutrition) => nutrition.value_low === nutrition.value_high) ?? false,
    [inventoryItem],
  );

  const nutritionColumns = useMemo(
    () => (hasSingleNutritionValue ? nutritionValueColumns : nutritionRangeColumns),
    [hasSingleNutritionValue],
  );

  return (
    <div className={styles['overview-container']}>
      <DetailsPanel title="Allergens & Claims">
        <DetailsPanel.Row>
          <DetailsPanel.Column>
            <FormatInformation heading="Allergens">{getAllergens(inventoryItem) || '-'}</FormatInformation>
            <FormatInformation heading="Claims">{getClaims(inventoryItem) || '-'}</FormatInformation>
            <FormatInformation heading="Vegan">{inventoryItem.is_vegan ? 'Yes' : 'No'}</FormatInformation>
            <FormatInformation heading="Vegetarian">{inventoryItem.is_vegetarian ? 'Yes' : 'No'}</FormatInformation>
          </DetailsPanel.Column>
        </DetailsPanel.Row>
      </DetailsPanel>
      <DetailsPanel title="Nutritional Information">
        <DetailsPanel.Row>
          <DetailsPanel.Column>
            <div className={styles['table-container']}>
              <Table
                className={styles['nutrition-table']}
                data={nutritionRows}
                columns={nutritionColumns}
                EmptyState={NutritionEmptyState}
              />
            </div>
          </DetailsPanel.Column>
        </DetailsPanel.Row>
      </DetailsPanel>
    </div>
  );
};

export default InventoryDietaryAttributesTab;
